import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton,
    InputAdornment, Link, Stack, TextField, Typography,
} from '@mui/material';
import axios from 'axios';
import React from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { ILogin, LoginApi } from '../api/LoginApi';
import { IUser, UserApi } from "../api/UserApi";
import { handleErrorMessage } from '../component/ErrorDialog';
import { VerbaScholaPublicHeader } from '../component/VerbaScholaPublicChrome';
import { landingForRole, useSignedInAccountState } from '../component/verbaScholaLanding';
import { isJwtLoggedIn } from '../util/session';
import AppBarContext from '../context/AppBarContext';
import {
    BLUE_TEXT, BORDER, INTER, NAVY, PAGE_BACKGROUND, PILOT_REQUEST_HREF, POPPINS, SLATE, SUPPORT_EMAIL,
    primaryButtonSx, publicCardSx,
} from '../style/verbaScholaPublicStyles';

function returnPathAfterLogin(from: unknown, role: IUser['role']): string | null {
    const value = typeof from === 'string' ? from : from && typeof from === 'object' && 'pathname' in from
        ? `${String(from.pathname)}${'search' in from ? String(from.search ?? '') : ''}${'hash' in from ? String(from.hash ?? '') : ''}` : null;
    if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null;
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || ['/login', '/registration'].includes(url.pathname)) return null;
    if ((url.pathname === '/teacher' || url.pathname.startsWith('/teacher/')) && role !== 'teacher' && role !== 'admin') return null;
    if ((url.pathname === '/student' || url.pathname.startsWith('/student/')) && (role === 'teacher' || role === 'admin')) return null;
    const legacyAdminSections = ['/admin', '/create_lesson', '/dialog_plan', '/course_offering', '/manual_grade_audio', '/user_admin', '/report', '/regen_audio'];
    if (role !== 'admin' && legacyAdminSections.some(section => url.pathname === section || url.pathname.startsWith(`${section}/`))) return null;
    return `${url.pathname}${url.search}${url.hash}`;
}

export function setUserLocalStorage(user: IUser) {
    if (user.email) {
        localStorage.setItem('userEmail', user.email);
    }
    if (user.first_name === undefined || user.first_name === null) {
        localStorage.removeItem('studentName');
    } else {
        localStorage.setItem('studentName', user.first_name);
    }
    if (user.learn_chinese_character === undefined || user.learn_chinese_character === null) {
        localStorage.setItem('learnWithCharacter', true.toString());
    } else {
        localStorage.setItem('learnWithCharacter', user.learn_chinese_character.toString());
    }
    if (user.avatar === undefined || user.avatar === null) {
        localStorage.removeItem('studentAvatar')
    } else {
        localStorage.setItem('studentAvatar', user.avatar);
    }
    if (user.chinese_teacher === undefined || user.chinese_teacher === null) {
        localStorage.removeItem('teacher')
    } else {
        localStorage.setItem('teacher', user.chinese_teacher);
    }
    if (user.only_required_audio === undefined || user.only_required_audio === null) {
        localStorage.setItem('onlyRequiredAudio', true.toString());
    } else {
        localStorage.setItem('onlyRequiredAudio', user.only_required_audio.toString());
    }
}

const linkSx = { fontFamily: INTER, fontSize: 14, fontWeight: 600, color: BLUE_TEXT } as const;
const CREDENTIALS_ERROR = 'That email and password don’t match. Check them and try again, or reset your password.';
const GENERAL_ERROR = 'We couldn’t log you in right now. Check your connection and try again.';

export default function Login() {
    const { setErrorMessage, setIsLoggedIn } = React.useContext(AppBarContext);
    const history = useHistory();
    const location = useLocation();
    const [alreadyLoggedIn, setAlreadyLoggedIn] = React.useState(() => isJwtLoggedIn());
    const [loginErrorMessage, setLoginErrorMessage] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [sessionEnded, setSessionEnded] = React.useState(() => location.state?.sessionEnded === true);
    const handleUnauthorizedAccount = React.useCallback(() => {
        setAlreadyLoggedIn(false);
        setSessionEnded(true);
    }, []);
    const { account: signedInAccount, unavailable: accountUnavailable, retry: retryAccount } = useSignedInAccountState(handleUnauthorizedAccount);
    const loggedInEmail = signedInAccount?.email ?? localStorage.getItem('userEmail');

    const errorRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (loginErrorMessage) errorRef.current?.focus();
    }, [loginErrorMessage]);

    const handleLogout = () => {
        localStorage.clear();
        setIsLoggedIn(false);
        setAlreadyLoggedIn(false);
        history.push('/login');
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (alreadyLoggedIn || isSubmitting) return;
        setLoginErrorMessage('');
        const data = new FormData(event.currentTarget);
        const loginObject: ILogin = {
            email: String(data.get('email')),
            password: String(data.get('password')),
            rememberMe: Boolean(data.get('rememberMe')),
        };
        setIsSubmitting(true);
        LoginApi.login(loginObject)
            .then(response => {
                if (response.status >= 300) {
                    setLoginErrorMessage(CREDENTIALS_ERROR);
                    return;
                }
                localStorage.setItem('jwt', response.data.token);
                setIsLoggedIn(true);
                return UserApi.get()
                    .then(userResponse => {
                        const user: IUser = userResponse.data;
                        setUserLocalStorage(user);
                        const requested = returnPathAfterLogin(location.state?.from, user.role);
                        history.replace(requested ?? landingForRole(user.role));
                    })
                    .catch(err => {
                        localStorage.removeItem('jwt');
                        setIsLoggedIn(false);
                        setLoginErrorMessage(GENERAL_ERROR);
                        handleErrorMessage(err, setErrorMessage, history, location);
                    });
            })
            .catch(err => {
                // A 401 means the credentials were rejected; the shared handler would only
                // redirect back here, so the message is shown inline instead.
                if (axios.isAxiosError(err) && err.response?.status === 401) {
                    setLoginErrorMessage(CREDENTIALS_ERROR);
                    return;
                }
                setLoginErrorMessage(GENERAL_ERROR);
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => setIsSubmitting(false));
    };

    const errorDescribedBy = loginErrorMessage ? 'login-error' : undefined;

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: PAGE_BACKGROUND }}>
            <VerbaScholaPublicHeader minimal />
            <Box component="main" sx={{ flex: 1, px: 2, py: { xs: 4, sm: 7 } }}>
                <Box sx={{ maxWidth: 440, mx: 'auto' }}>
                    <Box sx={{ ...publicCardSx, p: { xs: 3, sm: 4 }, boxShadow: '0 10px 30px rgba(22, 58, 99, 0.06)' }}>
                        {alreadyLoggedIn ? (
                            <Stack spacing={2} sx={{ textAlign: 'center' }}>
                                <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 24, color: NAVY }}>
                                    You’re already logged in
                                </Typography>
                                {loggedInEmail && (
                                    <Typography sx={{ fontFamily: INTER, fontSize: 15, color: SLATE }}>
                                        Logged in as <Box component="strong" sx={{ color: NAVY, wordBreak: 'break-all' }}>{loggedInEmail}</Box>
                                    </Typography>
                                )}
                                {accountUnavailable ? (
                                    <>
                                        <Alert severity="warning" role="status" sx={{ borderRadius: '12px', textAlign: 'left' }}>
                                            We couldn’t load your account right now. Check your connection and try again.
                                        </Alert>
                                        <Button onClick={retryAccount} sx={{ ...primaryButtonSx, minHeight: 48 }}>Try again</Button>
                                    </>
                                ) : (
                                    // The destination depends on the account role, so it waits for the account to load.
                                    <Button component={RouterLink} to={signedInAccount?.landing ?? '/login'} disabled={!signedInAccount} sx={{ ...primaryButtonSx, minHeight: 48 }}>
                                        {signedInAccount ? 'Continue to VerbaSchola' : 'Loading your account…'}
                                    </Button>
                                )}
                                <Button variant="text" onClick={handleLogout}>Log out</Button>
                            </Stack>
                        ) : (
                            <>
                                <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 26, color: NAVY, textAlign: 'center' }}>
                                    Log in
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 15, color: SLATE, mt: 0.75, textAlign: 'center' }}>
                                    Welcome back. Pick up where you left off.
                                </Typography>

                                {/* Notices, unlike login errors, leave the fields unmarked and keep focus on the email field. */}
                                {sessionEnded && !loginErrorMessage && (
                                    <Alert severity="info" role="status" sx={{ mt: 3, borderRadius: '12px' }}>
                                        Your session has ended. Please log in again.
                                    </Alert>
                                )}
                                {location.state?.passwordChanged && !loginErrorMessage && (
                                    <Alert severity="success" role="status" sx={{ mt: 3, borderRadius: '12px' }}>
                                        Password updated. Please log in again.
                                    </Alert>
                                )}
                                {loginErrorMessage && (
                                    <Alert severity="error" ref={errorRef} tabIndex={-1} id="login-error" sx={{ mt: 3, borderRadius: '12px', outline: 'none' }}>
                                        {loginErrorMessage}
                                    </Alert>
                                )}

                                <Stack component="form" spacing={2.25} onSubmit={handleSubmit} sx={{ mt: 3 }}>
                                    <TextField
                                        id="email"
                                        name="email"
                                        label="Email address"
                                        type="email"
                                        autoComplete="email"
                                        autoFocus
                                        required
                                        fullWidth
                                        error={Boolean(loginErrorMessage)}
                                        inputProps={{ 'aria-describedby': errorDescribedBy }}
                                    />
                                    <Box>
                                        <TextField
                                            id="password"
                                            name="password"
                                            label="Password"
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="current-password"
                                            required
                                            fullWidth
                                            error={Boolean(loginErrorMessage)}
                                            inputProps={{ 'aria-describedby': errorDescribedBy }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setShowPassword(value => !value)}
                                                            edge="end"
                                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                            aria-pressed={showPassword}
                                                        >
                                                            {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mt: 0.5 }}>
                                            <FormControlLabel
                                                control={<Checkbox id="rememberMe" name="rememberMe" />}
                                                label={<Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY }}>Remember me</Typography>}
                                                sx={{ minHeight: 44, mr: 1 }}
                                            />
                                            <Link component={RouterLink} to="/reset_password_email" underline="hover"
                                                sx={{ ...linkSx, minHeight: 44, display: 'inline-flex', alignItems: 'center' }}>
                                                Forgot password?
                                            </Link>
                                        </Stack>
                                    </Box>
                                    <Button type="submit" disabled={isSubmitting} sx={{ ...primaryButtonSx, minHeight: 48 }}>
                                        {isSubmitting
                                            ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} sx={{ color: 'inherit' }} aria-hidden /><span>Logging in…</span></Stack>
                                            : 'Log in'}
                                    </Button>
                                </Stack>

                                <Divider sx={{ my: 3, borderColor: BORDER }} />
                                <Stack spacing={0.5} sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE }}>
                                        New student?{' '}
                                        <Link component={RouterLink} to="/registration" underline="hover" sx={linkSx}>Create an account</Link>
                                    </Typography>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE }}>
                                        Teacher?{' '}
                                        <Link component={RouterLink} to={PILOT_REQUEST_HREF} underline="hover" sx={linkSx}>Request a pilot</Link>
                                    </Typography>
                                </Stack>
                            </>
                        )}
                    </Box>

                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, textAlign: 'center', mt: 3 }}>
                        Need help?{' '}
                        <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover" sx={{ ...linkSx, fontSize: 13 }}>{SUPPORT_EMAIL}</Link>
                        {' · '}
                        <Link component={RouterLink} to="/" underline="hover" sx={{ ...linkSx, fontSize: 13 }}>Back to home</Link>
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
